import React, { useState, useEffect } from 'react';
import {
  Modal,
  Input,
  Button,
  Upload,
  message,
  Typography,
  Space,
  Divider,
  Tooltip,
  Image,
  Spin,
} from 'antd';
import { BookOpen, UploadCloud } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import firebase from 'firebase/compat/app';
import Message, { Type } from 'components/Notification/Message';
import { AuditGuide } from 'modules/auditTemplates/types';
import { UploadContext } from 'general/constants';
import { getContentTypeIconMap } from 'Views/Reports/Report';
import { Button as ShadButton } from 'components/ui/button';
import FileCard from '../FileCard';
import { ContentType } from '@/general/enums';
import useGetDownloadUrl from 'hooks/useGetDownloadUrl';

interface InstructionsModalProps {
  orgId: string;
  templateId?: string;
  taskId: string;
  instructions?: string;
  guide?: AuditGuide | null;
  isReadOnly?: boolean;
}

function InstructionImagePreview({ guide }: { guide: AuditGuide }) {
  const { loading, fileUrl } = useGetDownloadUrl(guide.filePath);

  if (loading) {
    return (
      <div
        style={{
          alignItems: 'center',
          backgroundColor: '#fafafa',
          border: '1px solid #f0f0f0',
          borderRadius: 6,
          display: 'flex',
          height: 180,
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <Spin />
      </div>
    );
  }

  if (!fileUrl) {
    return <Typography.Text type="secondary">Billedet kunne ikke indlæses</Typography.Text>;
  }

  return (
    <Image
      alt={guide.fileName}
      src={fileUrl}
      style={{
        backgroundColor: '#fafafa',
        border: '1px solid #f0f0f0',
        borderRadius: 6,
        maxHeight: 320,
        objectFit: 'contain',
        width: '100%',
      }}
      width="100%"
    />
  );
}

export default function InstructionsModal({
  orgId,
  templateId = '',
  taskId,
  instructions: initialInstructions,
  guide: initialGuide,
  isReadOnly = false,
}: InstructionsModalProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [instructions, setInstructions] = useState(initialInstructions || '');
  const [guide, setGuide] = useState<AuditGuide | null>(initialGuide || null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (isReadOnly) return;
    if (initialGuide) {
      setGuide(initialGuide);
    }
  }, [initialGuide]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      Message({
        key: 'saveInstructions',
        type: Type.LOADING,
        message: t('Gemmer instruktioner...'),
      });

      await firebase
        .firestore()
        .collection('orgs')
        .doc(orgId)
        .collection('auditTemplates')
        .doc(templateId)
        .collection('tasks')
        .doc(taskId)
        .update({
          instructions,
          guide,
        });

      Message({
        key: 'saveInstructions',
        type: Type.SUCCESS,
        message: t('Instruktioner blev gemt'),
      });

      setIsOpen(false);
    } catch (error) {
      console.error(error);
      Message({
        key: 'saveInstructions',
        type: Type.ERROR,
        message: t('Der skete en fejl'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const uploadFile = ({ onSuccess, onError, file }: any): any => {
    setIsUploading(true);

    firebase
      .storage()
      .ref('orgs/' + orgId + '/auditTemplates')
      // @ts-ignore
      .putFiles(
        [file],
        orgId,
        file.name,
        UploadContext.AUDIT_TEMPLATE,
        `${templateId}/tasks/${taskId}`
      )
      .then(function (_: any) {
        message.success(t('Fil uploadet'));
        setIsUploading(false);
        // The guide will be updated via Firestore listener
        onSuccess();
      })
      .catch(function (error: Error) {
        console.log(error);
        setIsUploading(false);
        onError();
      });
  };

  const handleRemoveGuide = async () => {
    try {
      await firebase
        .firestore()
        .collection('orgs')
        .doc(orgId)
        .collection('auditTemplates')
        .doc(templateId)
        .collection('tasks')
        .doc(taskId)
        .update({
          guide: null,
        });

      setGuide(null);
      message.success(t('Fil fjernet'));
    } catch (error) {
      console.error(error);
      message.error(t('Der skete en fejl'));
    }
  };

  const icon = guide
    ? getContentTypeIconMap(guide.contentType, guide.filePath, {
        height: 60,
        fontSize: 60,
      })
    : null;

  const hasContent = initialInstructions || initialGuide;

  // if (!hasContent) {
  //   return null;
  // }

  return (
    <>
      <Tooltip title={t('Instruktioner')}>
        <ShadButton
          className="flex items-center h-8 shadow-none"
          variant="outline"
          size="icon"
          onClick={() => setIsOpen(true)}
        >
          <BookOpen strokeWidth={2} size={16} />
        </ShadButton>
      </Tooltip>

      <Modal
        title={t('Opgave instruktioner')}
        open={isOpen}
        width={600}
        onCancel={() => {
          setIsOpen(false);
          setInstructions(initialInstructions || '');
          setGuide(initialGuide || null);
        }}
        footer={
          isReadOnly
            ? [
                <Button key="close" onClick={() => setIsOpen(false)}>
                  {t('Luk')}
                </Button>,
              ]
            : [
                <Button key="cancel" onClick={() => setIsOpen(false)}>
                  {t('Annuller')}
                </Button>,
                <Button key="save" type="primary" loading={isSaving} onClick={handleSave}>
                  {t('Gem')}
                </Button>,
              ]
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Typography.Text strong>{t('Tekst instruktioner')}</Typography.Text>
            <Input.TextArea
              rows={6}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder={t('Tilføj detaljerede instruktioner til opgaven...')}
              disabled={isReadOnly}
              style={{
                marginTop: 8,
                ...(isReadOnly && {
                  backgroundColor: '#fff',
                  color: '#333',
                  cursor: 'not-allowed',
                }),
              }}
            />
          </div>

          <Divider style={{ margin: 0 }} />

          <div>
            {guide ? (
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                {guide.contentType?.startsWith('image/') && (
                  <InstructionImagePreview guide={guide} />
                )}
                <FileCard
                  icon={icon}
                  fileName={guide.fileName}
                  title={guide.fileName}
                  path={guide.filePath}
                  removeAuditTemplateFile={handleRemoveGuide}
                  contentType={guide.contentType as ContentType}
                  small
                  isReadOnly={isReadOnly}
                />
              </Space>
            ) : (
              <div>
                <Upload
                  name="file"
                  showUploadList={false}
                  customRequest={uploadFile}
                  disabled={isReadOnly}
                  style={{ marginTop: 8 }}
                >
                  <Button
                    icon={<UploadCloud size={16} />}
                    loading={isUploading}
                    disabled={isReadOnly}
                    style={{ marginTop: 8, width: '100%' }}
                  >
                    {t('Upload fil')}
                  </Button>
                </Upload>
              </div>
            )}

            {!isReadOnly && (
              <Typography.Text strong>
                {t('Vedh\u00e6ft fil (billede, video, dokument)')}
              </Typography.Text>
            )}
          </div>
        </Space>
      </Modal>
    </>
  );
}
